import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WavRecorder } from "../../Components/wavtools";
import {
  createCreation,
  startRecording,
  uploadArtifact,
  finalizeInput,
  deleteCreation,
  recordingSocketUrl,
  dciError,
  INPUT_METHOD,
} from "../../Services/dialogueCreations";
import {
  CaptureHeader,
  CapturePage,
  CaptureSheet,
  useFocusedCaptureHeader,
  makeIdempotencyKey,
} from "./shared";
import { RECORD_COPY, errorText } from "./copy";
import soundwaveIcon from "../../Assets/icon/capture/soundwave.svg";
import stopIcon from "../../Assets/icon/capture/stop.svg";

/**
 * Record Live (Figma 9856:14676 / 9856:14953; guide §4).
 *
 * 1. create (RECORDING) → `recording/start`. A first-ever recording without
 *    consent is 412 RECORDING_CONSENT_REQUIRED: the consent sheet shows, then
 *    the start is retried with `consent_acknowledged: true` (stored once).
 * 2. The socket at `stream_path` takes binary PCM16 mono @ 16 kHz frames of
 *    ~200 ms; the server sends `transcript.partial`, `warning`,
 *    `limit_reached` and finally `stopped` (analysis already scheduled).
 * 3. Stop = one text frame. A dropped socket is NOT a stop — the server keeps
 *    nothing — so this page keeps its own local copy of the audio and, if the
 *    socket drops before `stopped`, uploads it as an artifact and finalizes;
 *    the backend transcribes it in batch.
 *
 * Audio is captured with the app's vendored WavRecorder at 16 kHz, used as
 * is (the vendored library is not modified).
 */

const SAMPLE_RATE = 16000;
/** 200 ms of PCM16 mono at 16 kHz, in bytes. */
const FRAME_BYTES = SAMPLE_RATE * 2 * 0.2;

const clock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const r = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${r}`;
};

function CaptureRecord() {
  const navigate = useNavigate();
  useFocusedCaptureHeader();

  const [phase, setPhase] = useState("ready"); // ready | starting | recording | stopping | recovering
  const [elapsed, setElapsed] = useState(0);
  const [caption, setCaption] = useState("");
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);

  const key = useRef(makeIdempotencyKey("recording"));
  const creationId = useRef(null);
  const socket = useRef(null);
  const recorder = useRef(null);
  const timer = useRef(null);
  const stopped = useRef(false);
  const leaving = useRef(false);

  const clearTimer = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };

  /** Stop the microphone; returns the local WAV (for recovery) when asked. */
  const releaseMic = useCallback(async (keepAudio = false) => {
    const rec = recorder.current;
    recorder.current = null;
    if (!rec) return null;
    try {
      if (keepAudio) {
        try {
          await rec.pause();
        } catch (_) {
          /* already paused */
        }
        const wav = await rec.end();
        return wav?.blob || null;
      }
      await rec.quit();
    } catch (e) {
      console.error("[Capture] releasing the microphone failed", e);
    }
    return null;
  }, []);

  const goToFlow = useCallback(() => {
    const id = creationId.current;
    if (id) navigate(`/capture/${encodeURIComponent(id)}`, { replace: true });
  }, [navigate]);

  /** The socket dropped before `stopped`: save the local copy instead. */
  const recover = useCallback(async () => {
    if (leaving.current) return;
    clearTimer();
    setPhase("recovering");
    setNotice(RECORD_COPY.recovering);
    const blob = await releaseMic(true);
    const id = creationId.current;
    if (!blob || !id) {
      setPhase("ready");
      setNotice(null);
      setError(RECORD_COPY.lost);
      return;
    }
    try {
      await uploadArtifact(id, new File([blob], "recording.wav", { type: "audio/wav" }));
      await finalizeInput(id);
      goToFlow();
    } catch (e) {
      console.error("[Capture] recording recovery failed", e);
      setPhase("ready");
      setNotice(null);
      setError(RECORD_COPY.lost);
    }
  }, [goToFlow, releaseMic]);

  const openStream = useCallback(
    async (streamPath) => {
      const rec = new WavRecorder({ sampleRate: SAMPLE_RATE });
      try {
        await rec.begin();
      } catch (e) {
        setPhase("ready");
        setError(RECORD_COPY.micDenied);
        return;
      }
      recorder.current = rec;

      const ws = new WebSocket(recordingSocketUrl(streamPath));
      ws.binaryType = "arraybuffer";
      socket.current = ws;
      stopped.current = false;

      ws.onopen = async () => {
        setPhase("recording");
        const startedAt = Date.now();
        timer.current = window.setInterval(() => setElapsed((Date.now() - startedAt) / 1000), 500);
        await rec.record((data) => {
          if (ws.readyState === WebSocket.OPEN && data?.mono) ws.send(data.mono);
        }, FRAME_BYTES);
      };

      ws.onmessage = (evt) => {
        if (typeof evt.data !== "string") return;
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch (_) {
          return;
        }
        if (msg.type === "transcript.partial") setCaption(msg.text || "");
        else if (msg.type === "warning" && msg.minutes_remaining) {
          setNotice(RECORD_COPY.warning(msg.minutes_remaining));
        } else if (msg.type === "limit_reached") {
          setPhase("stopping");
          setNotice(RECORD_COPY.limit);
        } else if (msg.type === "stopped") {
          stopped.current = true;
          clearTimer();
          releaseMic(false);
          goToFlow();
        }
      };

      ws.onclose = (evt) => {
        socket.current = null;
        if (stopped.current || leaving.current) return;
        // 4404: flag off / not recordable. Nothing to recover into.
        if (evt.code === 4404) {
          clearTimer();
          releaseMic(false);
          setPhase("ready");
          setError(errorText(null));
          return;
        }
        recover();
      };
    },
    [goToFlow, recover, releaseMic]
  );

  const start = async (consent = false) => {
    setError(null);
    setNotice(null);
    setPhase("starting");
    try {
      if (!creationId.current) {
        const creation = await createCreation(INPUT_METHOD.RECORDING, key.current);
        creationId.current = creation?.creation_request_id;
      }
      const recording = await startRecording(creationId.current, consent);
      await openStream(recording?.stream_path);
    } catch (e) {
      const err = dciError(e);
      setPhase("ready");
      if (err.status === 412 || err.code === "RECORDING_CONSENT_REQUIRED") {
        setConsentOpen(true);
        return;
      }
      setError(errorText(err.code));
    }
  };

  const stop = () => {
    const ws = socket.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    setPhase("stopping");
    clearTimer();
    // No more frames after Stop; the local copy is kept for recovery.
    recorder.current?.pause().catch(() => {});
    ws.send(JSON.stringify({ type: "stop" }));
  };

  const cancel = async () => {
    leaving.current = true;
    clearTimer();
    try {
      socket.current?.close();
    } catch (_) {
      /* already closed */
    }
    await releaseMic(false);
    const id = creationId.current;
    if (id) deleteCreation(id).catch(() => {});
    navigate("/capture");
  };

  // Leaving the page mid-recording releases the microphone and the socket.
  useEffect(
    () => () => {
      leaving.current = true;
      clearTimer();
      try {
        socket.current?.close();
      } catch (_) {
        /* already closed */
      }
      if (recorder.current) recorder.current.quit().catch(() => {});
    },
    []
  );

  const live = phase === "recording" || phase === "stopping" || phase === "recovering";

  return (
    <CapturePage focused>
      <CaptureHeader title={RECORD_COPY.title} onBack={cancel} />
      <div className="cap-body cap-record">
        <span className={`cap-record__orb${phase === "recording" ? " cap-record__orb--live" : ""}`}>
          <img src={soundwaveIcon} alt="" width="83" height="83" />
        </span>

        {live ? (
          <div className="cap-record__text">
            <p className="cap-record__clock">{clock(elapsed)}</p>
            <p className="cap-record__status">
              {phase === "recording" ? RECORD_COPY.recording : RECORD_COPY.stopping}
            </p>
            <p className="cap-record__hint">{RECORD_COPY.recordingBody}</p>
            {caption && <p className="cap-record__caption">{caption}</p>}
          </div>
        ) : (
          <div className="cap-record__text">
            <p className="cap-record__ready">{RECORD_COPY.readyTitle}</p>
            <p className="cap-record__hint">{RECORD_COPY.readyBody}</p>
          </div>
        )}

        {notice && <p className="cap-note">{notice}</p>}
        {error && <p className="cap-error">{error}</p>}

        {live ? (
          <button
            type="button"
            className="cap-btn cap-btn--danger"
            disabled={phase !== "recording"}
            onClick={stop}
          >
            <img src={stopIcon} alt="" width="24" height="24" />
            {RECORD_COPY.stop}
          </button>
        ) : (
          <button
            type="button"
            className="cap-btn"
            disabled={phase === "starting"}
            onClick={() => start(false)}
          >
            <span className="cap-btn__dot" aria-hidden="true" />
            {RECORD_COPY.start}
          </button>
        )}
        <button type="button" className="cap-link" onClick={cancel} disabled={phase === "recovering"}>
          {RECORD_COPY.cancel}
        </button>
      </div>

      <CaptureSheet
        open={consentOpen}
        title={RECORD_COPY.consentTitle}
        onClose={() => setConsentOpen(false)}
      >
        <p className="cap-sheet__body">{RECORD_COPY.consentBody}</p>
        <div className="cap-actions">
          <button
            type="button"
            className="cap-btn"
            onClick={() => {
              setConsentOpen(false);
              start(true);
            }}
          >
            {RECORD_COPY.consentAccept}
          </button>
          <button type="button" className="cap-link" onClick={() => setConsentOpen(false)}>
            {RECORD_COPY.cancel}
          </button>
        </div>
      </CaptureSheet>
    </CapturePage>
  );
}

export default CaptureRecord;
